import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@ui/components/ui/badge";
import { Button } from "@ui/components/ui/button";
import { PRODUCT_TYPE_DISPLAY_GROUPS } from "@modules/shop/components/Products/FilterSidebar";
import type { useProductFilters } from "@modules/shop/hooks/useProductFilters";

type ActiveFilterPillsProps = {
  productFilters: ReturnType<typeof useProductFilters>;
};

export function ActiveFilterPills({
  productFilters,
}: Readonly<ActiveFilterPillsProps>) {
  const { t } = useTranslation();
  const { filters, setType, toggleColor, toggleSize, resetFilters } =
    productFilters;

  const pills: Array<{ label: string; onRemove: () => void }> = [];

  // Build one pill per active display group (not one per individual type value).
  // This mirrors how the sidebar presents them: one checkbox = one group.
  const handledTypes = new Set<string>();
  const typeTitle = t("Filters.productType.title");

  for (const { labelKey, values } of PRODUCT_TYPE_DISPLAY_GROUPS) {
    const activeInGroup = (values as ReadonlyArray<string>).filter((v) =>
      filters.type.includes(v),
    );
    if (activeInGroup.length === 0) continue;

    activeInGroup.forEach((v) => handledTypes.add(v));
    const groupLabel = t(`Filters.productType.groups.${labelKey}` as never);
    const groupValues = values as ReadonlyArray<string>;

    pills.push({
      label: `${typeTitle}: ${groupLabel}`,
      onRemove: () => {
        void setType(filters.type.filter((v) => !groupValues.includes(v)));
      },
    });
  }

  // Fallback: any active type not covered by a display group (e.g. manual URL edit)
  for (const type of filters.type) {
    if (handledTypes.has(type)) continue;
    const capturedType = type;
    pills.push({
      label: `${typeTitle}: ${capturedType}`,
      onRemove: () => {
        void setType(filters.type.filter((v) => v !== capturedType));
      },
    });
  }

  const colorTitle = t("Filters.color.title");
  for (const color of filters.color) {
    const colorLabel = t(`Filters.color.${color}` as never);
    pills.push({
      label: `${colorTitle}: ${colorLabel}`,
      onRemove: () => toggleColor(color),
    });
  }

  for (const size of filters.size) {
    pills.push({
      label: `${t("Filters.size.title")}: ${size}`,
      onRemove: () => toggleSize(size),
    });
  }

  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap items-start gap-2">
      <ul
        className="flex flex-wrap items-center gap-2 list-none p-0 m-0"
        aria-label={t("Filters.activeFilters")}
      >
        {pills.map((pill) => (
          <li key={pill.label}>
            <button
              type="button"
              onClick={pill.onRemove}
              aria-label={`Remove filter: ${pill.label}`}
              className="group"
            >
              <Badge className="inline-flex cursor-pointer items-center gap-1.5 transition-colors group-hover:border-border-3 group-hover:bg-background-7">
                {pill.label}
                <X
                  className="text-text-4 group-hover:text-text-1 size-3.5 shrink-0"
                  aria-hidden="true"
                />
              </Badge>
            </button>
          </li>
        ))}
      </ul>

      {pills.length > 1 && (
        <Button primary="ghost" size="extraSmall" onClick={resetFilters}>
          {t("Filters.clearAll")}
        </Button>
      )}
    </div>
  );
}
